import { foundation } from '../../../ui';
import { semantic, components } from '../../../ui/theme';
import { GROUPED_GEOMETRY, GROUPED_TRAILING_PX, groupedLeadingSlot, groupedCheckColStyle } from '../../../ui/groupedListGeometry';
import { semantic as sem } from '../../../ui/theme/semanticTokens';

export { groupedLeadingSlot, groupedCheckColStyle, GROUPED_TRAILING_PX };

const { color: fc } = foundation;

/** Shared look of the month → customer → record grouped lists (KAN-1016). */
export const GROUPED_LIST = {
  monthInk: fc.neutral.text,
  yearInk: fc.neutral.textMuted,
  metaInk: fc.neutral.textMuted,
  metaStrongInk: semantic.fg.ink2,
  monthFontPx: 15,
  monthRowMinPx: 48,
  openBand: semantic.groupedList.tint,
  openLine: sem.edge.open,
  openNameInk: fc.neutral.text,
  groupLine: semantic.groupedList.divider,
  rowLine: semantic.homeEdge.hairline,
  columnHeadBg: semantic.groupedList.tintFaint,
  columnHeadInk: semantic.groupedList.ink,
  card: {
    background: semantic.bg.surface,
    border: `1px solid ${sem.edge.hair06}`,
    // The shared list-card radius token (same string the Drafts card uses), so the selected-row
    // pill never touches the card's corners (KAN-1054).
    borderRadius: components.card.radius,
    boxShadow: semantic.elevation.widget,
  },
  customerRowMinPx: { web: 44, tablet: 52, phone: 56 },
  checkSlot: GROUPED_GEOMETRY.checkSlot,
  edgePx: GROUPED_GEOMETRY.edgePx,
  easeOut: 'cubic-bezier(0.23, 1, 0.32, 1)',
};

export const groupedColumnHeadCellStyle = {
  background: GROUPED_LIST.columnHeadBg,
  color: GROUPED_LIST.columnHeadInk,
  height: 40,
  fontSize: 12,
  fontWeight: 600,
  textAlign: 'left',
  verticalAlign: 'middle',
  borderTop: `1px solid ${GROUPED_LIST.openLine}`,
  borderBottom: `1px solid ${GROUPED_LIST.rowLine}`,
};

/** Width the checkbox slot takes at the start of month / customer / record rows. */
export function groupedCheckSlotPx(isTouch) {
  const s = isTouch ? GROUPED_LIST.checkSlot.touch : GROUPED_LIST.checkSlot.web;
  return s.box + s.gap;
}

export function groupedEdgePx({ isPhone, isTouch }) {
  if (isPhone) return GROUPED_LIST.edgePx.phone;
  return isTouch ? GROUPED_LIST.edgePx.tablet : GROUPED_LIST.edgePx.web;
}

export function groupedChevronStyle(expanded) {
  return {
    flexShrink: 0,
    color: expanded ? foundation.color.brand.text : foundation.color.neutral.iconMuted,
    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
    transformOrigin: '50% 50%',
    transition: `transform 200ms ${GROUPED_LIST.easeOut}, color 150ms ease`,
  };
}

/** Shared key/click rules for the two disclosure headers. */
export function disclosureHandlers(onToggle) {
  const fromLeading = (e) => !!e.target.closest?.('[data-cvx-group-leading]');
  return {
    onClick: (e) => { if (!fromLeading(e)) onToggle(); },
    onKeyDown: (e) => {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      if (fromLeading(e)) return;
      e.preventDefault();
      onToggle();
    },
  };
}

/**
 * The ONE trailing column of a grouped row (KAN-954): a 44px box holding the chevron, so the amount
 * before it ends on the same right edge as a Drafts row's 44px ⋮ column. Touch rows cancel the
 * excess over the row's own 36px content with a negative margin, so the row keeps its height.
 */
export function groupedTrailingStyle(compact) {
  return {
    width: GROUPED_TRAILING_PX, minWidth: GROUPED_TRAILING_PX, flex: 'none', boxSizing: 'border-box',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    ...(compact ? { height: GROUPED_TRAILING_PX, margin: '-4px 0' } : null),
  };
}

/** First data cell: starts at the edge, or right after the checkbox slot when one precedes it. */
export function groupedFirstCellPad(edge, hasCheckCol) {
  return { paddingLeft: hasCheckCol ? 0 : edge };
}
